import { Search, ChevronDown, Filter, Star, Loader2 } from "lucide-react";
import { useState, useMemo, useEffect, useCallback } from "react";
import {
  conversationResourcesChangedEvent,
  fetchConversationStripeResources,
} from "@/services/conversationResourceService";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Page } from "@/components/layout";
import { resourcesService } from "@/services/resourcesService";
import { Resource } from "@/types/resource";
import { useAuth } from "@/hooks/useAuth";
import { ResourceScrollOrGrid, ResourceShelfSection } from "@/components/resources/ResourceShelf";
import {
  savedDecisionsAsResources,
  savedDecisionsChangedEvent,
} from "@/services/savedDecisionsService";

export const ResourcesPage = () => {
  const { userData } = useAuth();
  const [resources, setResources] = useState<Record<string, Resource[]>>({});
  const [favoriteIds, setFavoriteIds] = useState<string[]>(resourcesService.getFavorites(userData?.user_id) || []);
  const [isLoading, setIsLoading] = useState(true);
  const [conversationStripe, setConversationStripe] = useState<Resource[]>([]);
  const [savedDecisionsStripe, setSavedDecisionsStripe] = useState<Resource[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All Resources");

  const categories = useMemo(() => Object.keys(resources), [resources]);

  const mergedYourResourcesStripe = useMemo(
    () => [...savedDecisionsStripe, ...conversationStripe],
    [savedDecisionsStripe, conversationStripe]
  );

  const loadConversationStripe = useCallback(async () => {
    if (!userData?.user_id) {
      setConversationStripe([]);
      return;
    }
    const next = await fetchConversationStripeResources();
    setConversationStripe(next);
  }, [userData?.user_id]);

  useEffect(() => {
    setSavedDecisionsStripe(savedDecisionsAsResources(userData?.user_id));
  }, [userData?.user_id]);

  useEffect(() => {
    void loadConversationStripe();

    const onRefresh = () => {
      void loadConversationStripe();
    };
    const onSavedDecisionsChanged = () => {
      setSavedDecisionsStripe(savedDecisionsAsResources(userData?.user_id));
    };
    window.addEventListener(conversationResourcesChangedEvent, onRefresh);
    window.addEventListener(savedDecisionsChangedEvent, onSavedDecisionsChanged);

    return () => {
      window.removeEventListener(conversationResourcesChangedEvent, onRefresh);
      window.removeEventListener(savedDecisionsChangedEvent, onSavedDecisionsChanged);
    };
  }, [loadConversationStripe, userData?.user_id]);

  const loadGhostLibrary = useCallback(() => {
    setIsLoading(true);
    resourcesService.clearCache();

    void resourcesService
      .getResources()
      .then((posts) => setResources(getCategoryResources(posts)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadGhostLibrary();
  }, [loadGhostLibrary]);

  const toggleFavorite = (id: string) => {
    if (!userData?.user_id) return;

    resourcesService.toggleUserFavorite(userData.user_id, id);
    const newFavoriteIds = favoriteIds.includes(id)
      ? favoriteIds.filter((favoriteId) => favoriteId !== id)
      : [...favoriteIds, id];
    setFavoriteIds(newFavoriteIds);
    if (newFavoriteIds.length === 0) {
      setSelectedCategory("All Resources");
    }
  };

  const filteredCategoryResources = useMemo(() => {
    let filtered: Record<string, Resource[]> = { ...resources };

    if (searchTerm.trim()) {
      const searchFiltered: Record<string, Resource[]> = {};

      Object.entries(resources).forEach(([category, bucket]) => {
        const matchingResources = bucket.filter(
          (resource) =>
            resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            category.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (matchingResources.length > 0) {
          searchFiltered[category] = matchingResources;
        }
      });

      filtered = searchFiltered;
    }

    if (selectedCategory === "Favorites") {
      filtered = {
        Favorites: Object.values(filtered)
          .flat()
          .filter((resource) => favoriteIds.includes(resource.id)),
      };
      return filtered;
    }

    if (selectedCategory !== "All Resources") {
      const categoryKey = Object.keys(filtered).find(
        (key) =>
          key.toLowerCase().includes(selectedCategory.toLowerCase().split(" ")[0]?.toLowerCase() ?? "") ||
          selectedCategory.toLowerCase().includes(key.toLowerCase().split(" ")[0]?.toLowerCase() ?? "")
      );

      if (categoryKey) {
        filtered = { [categoryKey]: filtered[categoryKey]! };
      } else {
        filtered = {};
      }
    }

    return filtered;
  }, [searchTerm, selectedCategory, resources, favoriteIds]);

  const ghostHasRows = categories.length > 0;

  return (
    <Page.Container>
      <Page.Header className="flex flex-col">
        <h1 className="font-literata text-2xl font-semibold text-white md:text-3xl">My Resources</h1>
        <p className="text-sm text-white/70 md:text-base">Personalized picks from your arc, plus a curated HeyBodhi library.</p>
      </Page.Header>

      <Page.Content className="space-y-12">
        <ResourceShelfSection
          title="Personalized Resources"
          subtitle="Past Decisions from Strategic Decision Partner, session-built resources, and toolkit outputs — all openable here."
          moreLink={{ to: "/growth/insights", label: "See your full arc" }}
          resources={mergedYourResourcesStripe}
          favoriteIds={favoriteIds}
          onFavoriteClick={toggleFavorite}
        />

        <div className="space-y-4">
          <div className="flex flex-col gap-0.5 px-2 md:px-0">
            <h2 className="font-literata text-lg font-semibold text-white md:text-xl">Library</h2>
            <p className="font-inter text-xs italic text-white/70 md:text-sm">
              Curated practices, scripts, and resets from HeyBodhi.
            </p>
          </div>

          <div className="flex flex-col items-center gap-1 sm:flex-row sm:gap-4">
            <div className="flex w-full items-center justify-center sm:w-auto md:px-0">
              <DropdownMenu>
                <DropdownMenuTrigger
                  asChild
                  className="flex w-full justify-start focus:outline-hidden focus-visible:ring-0 sm:justify-center"
                >
                  <Button
                    disabled={isLoading}
                    variant="outline"
                    className="flex w-full items-center space-x-2 border-white/20 bg-white/10 px-3 py-3 text-sm text-white hover:bg-white/20 hover:text-white focus-visible:border-bodhi-orange sm:py-2 md:text-base"
                  >
                    <Filter className="h-4 w-4" />
                    <span className="mr-auto flex-1 text-left">{selectedCategory}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  className="z-50 w-[84vw] rounded-lg border border-gray-200 bg-white shadow-lg sm:min-w-[250px] sm:w-auto"
                  align="start"
                >
                  <DropdownMenuItem
                    onClick={() => setSelectedCategory("All Resources")}
                    className={`cursor-pointer px-4 py-2 hover:bg-gray-100 ${
                      selectedCategory === "All Resources"
                        ? "bg-bodhi-orange/10 font-medium text-bodhi-orange"
                        : "text-gray-700"
                    }`}
                  >
                    All Resources
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    disabled={favoriteIds.length === 0}
                    onClick={() => setSelectedCategory("Favorites")}
                    className={`cursor-pointer px-4 py-2 hover:bg-gray-100 disabled:cursor-not-allowed ${
                      selectedCategory === "Favorites"
                        ? "bg-bodhi-orange/10 font-medium text-bodhi-orange"
                        : "text-gray-700"
                    }`}
                  >
                    Favorites <Star className="ml-2 h-3 w-3" />
                  </DropdownMenuItem>

                  {categories.map((category, index) => (
                    <DropdownMenuItem
                      key={index}
                      onClick={() => setSelectedCategory(category)}
                      className={`cursor-pointer px-4 py-2 hover:bg-gray-100 ${
                        selectedCategory === category ? "bg-bodhi-orange/10 font-medium text-bodhi-orange" : "text-gray-700"
                      }`}
                    >
                      {category}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="relative mx-auto w-full sm:max-w-md md:mx-0">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-white" />
              <input
                disabled={isLoading}
                type="text"
                placeholder="Search library…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-white/10 py-2 pl-10 pr-4 text-sm text-white transition-all placeholder:text-white/50 focus:border-bodhi-orange focus:bg-white/15 focus:outline-none sm:text-base"
              />
            </div>
          </div>
        </div>

        {isLoading && (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="flex items-center gap-3 text-white/90">
              <Loader2 className="h-7 w-7 shrink-0 animate-spin" aria-hidden />
              <span className="font-inter text-sm md:text-base">Loading library from HeyBodhi…</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => loadGhostLibrary()}
              className="border-white/30 bg-white/10 text-white hover:bg-white/15"
            >
              Reload library
            </Button>
          </div>
        )}

        {!isLoading && !ghostHasRows && (
          <div className="flex justify-center py-8">
            <Button type="button" onClick={() => loadGhostLibrary()} className="bg-white text-gray-900 hover:bg-white/90">
              Reload library
            </Button>
          </div>
        )}

        {Object.entries(filteredCategoryResources).map(([categoryName, bucket]) => (
          <div key={categoryName} className="space-y-6 px-2 md:px-0">
            <div className="flex items-center justify-between px-2 md:px-0">
              <h3 className="font-literata text-base font-semibold text-white md:text-xl">{categoryName}</h3>
            </div>

            <ResourceScrollOrGrid
              resources={bucket}
              favoriteIds={favoriteIds}
              onFavoriteClick={toggleFavorite}
              classNameDesktop="mt-2 md:mt-4"
              classNameMobile="mt-2"
            />
          </div>
        ))}

        {Object.keys(filteredCategoryResources).length === 0 && searchTerm.trim() !== "" && !isLoading && (
          <div className="py-12 text-center">
            <p className="text-lg text-white/70">
              No library resources match "{searchTerm}".
            </p>
            <p className="mt-2 text-sm text-white/50">Try another word or widen the filters above.</p>
          </div>
        )}
      </Page.Content>
    </Page.Container>
  );
};

/** Buckets Ghost resources by taxonomy tag `#category#...` (fallback: `Library`). */
const getCategoryResources = (posts: Resource[]): Record<string, Resource[]> => {
  const categoryResources: Record<string, Resource[]> = {};

  posts.forEach((post) => {
    const { photo, addedDate, title, type, category, url, description, meta, id } = post;
    const safeCategory = (category ?? "Library").trim() || "Library";

    categoryResources[safeCategory] = [
      ...(categoryResources[safeCategory] || []),
      { title, description, photo, meta, type, url, addedDate, id },
    ];
  });

  return categoryResources;
};
